import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Lock, Check, X, RefreshCw } from "lucide-react";

const STATUS_COLORS = {
  pending_admin_approval: "bg-[#FFD600] text-[#0A0A0A]",
  approved: "bg-[#16A34A] text-white",
  rejected: "bg-[#E53935] text-white",
};

export default function AdminCodeChanges() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending_admin_approval");
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/support/code-changes${q}`);
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, isAdmin]);

  const decide = async (decision) => {
    if (!sel) return;
    setActing(true);
    try {
      await api.post(`/support/tickets/${sel.id}/code-decision`, { decision, note });
      toast.success(decision === "approved" ? "Cambio aprobado" : "Cambio rechazado");
      setSel(null); setNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setActing(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="bg-white border border-[#0A0A0A] p-8 flex items-center gap-3" data-testid="admin-code-noaccess">
        <Lock className="h-5 w-5" />
        <span className="font-mono-data text-sm">Requiere permisos de administrador.</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-code-changes">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">CIA · ADMIN · AUTO-REPAIR</div>
          <h1 className="font-display text-4xl">Cambios de código</h1>
          <p className="font-mono-data text-sm text-[#5C5C5C] mt-1">
            Tickets que la IA clasificó como bugs reales. Revisa y decide si avanzan a desarrollo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-[#0A0A0A] bg-white px-3 h-9 font-mono-data text-sm"
            data-testid="code-filter"
          >
            <option value="pending_admin_approval">Pendientes</option>
            <option value="approved">Aprobados</option>
            <option value="rejected">Rechazados</option>
            <option value="">Todos</option>
          </select>
          <button
            onClick={load}
            className="border border-[#0A0A0A] bg-white h-9 px-3 font-mono-mark text-[10px] hover:bg-[#F4F4F0] flex items-center gap-1"
            data-testid="code-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> REFRESCAR
          </button>
        </div>
      </div>

      {loading ? (
        <div className="font-mono-data text-sm">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#0A0A0A] p-8 text-center font-mono-data text-sm" data-testid="code-empty">
          No hay tickets en este estado.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((t) => (
            <button
              key={t.id}
              onClick={() => { setSel(t); setNote(t.code_change_note || ""); }}
              className="bg-white border border-[#0A0A0A] p-4 text-left hover:bg-[#F4F4F0] brutal-shadow-sm transition"
              data-testid={`code-item-${t.id.slice(0, 8)}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono-mark text-[10px] text-[#5C5C5C]">
                    #{t.id.slice(0, 8)} · {t.email}
                  </div>
                  <div className="font-display text-lg mt-1 truncate">{t.subject}</div>
                  <div className="font-mono-data text-xs text-[#5C5C5C] mt-1 line-clamp-2">
                    {t.auto_repair?.summary}
                  </div>
                </div>
                <span className={`font-mono-mark text-[10px] px-2 py-1 border border-[#0A0A0A] ${STATUS_COLORS[t.code_change_status] || "bg-[#E5E5E5]"}`}>
                  {(t.code_change_status || "").toUpperCase()}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {sel && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setSel(null)}
          data-testid="code-modal"
        >
          <div
            className="bg-white border border-[#0A0A0A] max-w-2xl w-full p-6 max-h-[90vh] overflow-auto brutal-shadow"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono-mark text-[10px] text-[#5C5C5C]">#{sel.id.slice(0, 8)} · {sel.email}</div>
                <h2 className="font-display text-2xl mt-1">{sel.subject}</h2>
              </div>
              <button onClick={() => setSel(null)} className="border border-[#0A0A0A] p-1" data-testid="code-close"><X className="h-4 w-4" /></button>
            </div>

            <div className="mt-4 space-y-3 font-mono-data text-sm">
              <div>
                <div className="font-mono-mark text-[10px] text-[#5C5C5C]">RESUMEN IA</div>
                <div className="mt-1">{sel.auto_repair?.summary || "—"}</div>
              </div>
              <div>
                <div className="font-mono-mark text-[10px] text-[#5C5C5C]">PROPUESTA DE CAMBIO</div>
                <div className="mt-1 whitespace-pre-wrap">{sel.auto_repair?.code_change_proposal || "—"}</div>
              </div>
              <div>
                <div className="font-mono-mark text-[10px] text-[#5C5C5C]">MENSAJE DEL CLIENTE</div>
                <blockquote className="mt-1 border-l-2 border-[#E53935] pl-3 text-[#0A0A0A]/80">{sel.message}</blockquote>
              </div>
              <div>
                <div className="font-mono-mark text-[10px] text-[#5C5C5C]">NOTA ADMIN (opcional)</div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className="mt-1 w-full border border-[#0A0A0A] bg-white p-2 text-sm"
                  placeholder="Ej: lo paso al sprint de la semana 18"
                  data-testid="code-note"
                />
              </div>
            </div>

            {sel.code_change_status === "pending_admin_approval" ? (
              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => decide("approved")}
                  disabled={acting}
                  className="flex-1 bg-[#16A34A] text-white font-mono-mark text-[10px] py-3 border-2 border-[#0A0A0A] hover-brutal flex items-center justify-center gap-2"
                  data-testid="code-approve"
                >
                  <Check className="h-4 w-4" /> APROBAR CAMBIO
                </button>
                <button
                  onClick={() => decide("rejected")}
                  disabled={acting}
                  className="flex-1 bg-[#E53935] text-white font-mono-mark text-[10px] py-3 border-2 border-[#0A0A0A] hover-brutal flex items-center justify-center gap-2"
                  data-testid="code-reject"
                >
                  <X className="h-4 w-4" /> RECHAZAR
                </button>
              </div>
            ) : (
              <div className="mt-5 font-mono-data text-xs text-[#5C5C5C]">
                Ya decidido como <b>{sel.code_change_status}</b> {sel.code_change_decided_at && `el ${new Date(sel.code_change_decided_at).toLocaleString()}`}.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
